import { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  IconButton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import Loader from '../../components/Loader';
import { Settings } from '../../client/services';

const SETTING_NAME = 'dynamic_word';

type WordPair = {
  en: string;
  ar: string;
};

const emptyRow = (): WordPair => ({ en: '', ar: '' });

const trimWord = (value: unknown): string => String(value ?? '').trim();

const parsePairs = (raw: unknown): WordPair[] => {
  if (raw == null || raw === '') return [emptyRow()];
  try {
    const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw;

    if (Array.isArray(parsed)) {
      if (
        parsed.length > 0 &&
        parsed.every(
          (item) =>
            item &&
            typeof item === 'object' &&
            !Array.isArray(item) &&
            ('en' in item || 'ar' in item)
        )
      ) {
        const rows = parsed.map((item: any) => ({
          en: trimWord(item?.en),
          ar: trimWord(item?.ar),
        }));
        return rows.length ? rows : [emptyRow()];
      }
      const rows = parsed
        .map((item) => ({ en: trimWord(item), ar: '' }))
        .filter((row) => row.en);
      return rows.length ? rows : [emptyRow()];
    }

    if (parsed && typeof parsed === 'object') {
      const enList = Array.isArray(parsed.en) ? parsed.en.map(trimWord) : [];
      const arList = Array.isArray(parsed.ar) ? parsed.ar.map(trimWord) : [];
      const max = Math.max(enList.length, arList.length);
      const rows: WordPair[] = [];
      for (let i = 0; i < max; i += 1) {
        rows.push({ en: enList[i] || '', ar: arList[i] || '' });
      }
      return rows.length ? rows : [emptyRow()];
    }
  } catch {
    const text = trimWord(raw);
    if (text) return [{ en: text, ar: '' }];
  }
  return [emptyRow()];
};

const stringifyPairs = (rows: WordPair[]): string =>
  JSON.stringify(
    rows
      .map((row) => ({ en: row.en.trim(), ar: row.ar.trim() }))
      .filter((row) => row.en || row.ar)
  );

const headerLabelSx = {
  color: 'text.secondary',
  fontSize: 13,
  fontWeight: 400,
};

const DynamicWordPage = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [settingId, setSettingId] = useState<string | null>(null);
  const [savedValue, setSavedValue] = useState('[]');
  const [rows, setRows] = useState<WordPair[]>([emptyRow()]);

  const loadData = async () => {
    setIsLoading(true);
    try {
      const response = await Settings.find({
        query: { search: SETTING_NAME, limit: 50, skip: 0 },
      });
      const list = response?.data || [];
      const row =
        list.find((item: any) => item?.name === SETTING_NAME) || null;
      const nextRows = parsePairs(row?.value);
      const nextValue = stringifyPairs(nextRows);
      setSettingId(row?.id || null);
      setRows(nextRows);
      setSavedValue(nextValue);
    } catch (error: any) {
      toast.error(error?.message || 'Failed to load dynamic words');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const handleRowChange = (
    index: number,
    field: keyof WordPair,
    value: string
  ) => {
    setRows((prev) =>
      prev.map((row, rowIndex) =>
        rowIndex === index ? { ...row, [field]: value } : row
      )
    );
  };

  const handleAddRow = () => {
    setRows((prev) => [...prev, emptyRow()]);
  };

  const handleRemoveRow = (index: number) => {
    setRows((prev) => {
      if (prev.length === 1) return [emptyRow()];
      return prev.filter((_, rowIndex) => rowIndex !== index);
    });
  };

  const handleCancel = () => {
    setRows(parsePairs(savedValue));
  };

  const handleSave = async () => {
    const incomplete = rows.some(
      (row) => (row.en.trim() && !row.ar.trim()) || (!row.en.trim() && row.ar.trim())
    );
    if (incomplete) {
      toast.error('Each row needs both English and Arabic (or leave both empty)');
      return;
    }

    const value = stringifyPairs(rows);
    setIsSaving(true);
    try {
      if (settingId) {
        await Settings.patch(settingId, {
          name: SETTING_NAME,
          value,
          valueType: 'json',
          language: '',
          activeState: true,
        });
      } else {
        const created = await Settings.create({
          name: SETTING_NAME,
          value,
          valueType: 'json',
          language: '',
          activeState: true,
        });
        setSettingId(created?.id || null);
      }
      setSavedValue(value);
      setRows(parsePairs(value).length ? parsePairs(value) : [emptyRow()]);
      toast.success('Dynamic words saved');
      await loadData();
    } catch (error: any) {
      toast.error(error?.message || 'Failed to save dynamic words');
    } finally {
      setIsSaving(false);
    }
  };

  const isDirty = stringifyPairs(rows) !== savedValue;

  return (
    <Page title='Dynamic Word'>
      <Container
        maxWidth='xl'
        sx={{ minWidth: 0, px: { xs: 1.5, sm: 2, md: 3 } }}
      >
        <HeaderBreadcrumbs
          heading='Dynamic Word'
          links={[
            { name: 'Dashboard', href: '/' },
            { name: 'Dynamic Word' },
          ]}
        />

        <Card
          sx={{ p: { xs: 1.5, sm: 2, md: 3 }, minWidth: 0, overflow: 'visible' }}
        >
          {isLoading ? (
            <Loader />
          ) : (
            <Stack spacing={2.5}>
              <Box>
                <Typography variant='h6' sx={{ fontWeight: 700 }}>
                  Words
                </Typography>
              </Box>

              <Box sx={{ width: '100%', minWidth: 0 }}>
                <Stack
                  direction='row'
                  spacing={1.5}
                  alignItems='center'
                  sx={{
                    px: 0.5,
                    mb: 1,
                    display: { xs: 'none', sm: 'flex' },
                  }}
                >
                  <Typography sx={{ flex: 1, minWidth: 0, ...headerLabelSx }}>
                    English
                  </Typography>
                  <Typography sx={{ flex: 1, minWidth: 0, ...headerLabelSx }}>
                    Arabic
                  </Typography>
                  <Box sx={{ width: 40, flexShrink: 0 }} />
                </Stack>

                <Stack spacing={{ xs: 1.5, sm: 1 }}>
                  {rows.map((row, index) => (
                    <Stack
                      key={`dynamic-word-${index}`}
                      direction={{ xs: 'column', sm: 'row' }}
                      spacing={{ xs: 1.25, sm: 1.5 }}
                      alignItems={{ xs: 'stretch', sm: 'center' }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            ...headerLabelSx,
                            mb: 0.5,
                            display: { xs: 'block', sm: 'none' },
                          }}
                        >
                          English
                        </Typography>
                        <TextField
                          size='small'
                          fullWidth
                          placeholder='English'
                          value={row.en}
                          onChange={(e) =>
                            handleRowChange(index, 'en', e.target.value)
                          }
                        />
                      </Box>

                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Typography
                          sx={{
                            ...headerLabelSx,
                            mb: 0.5,
                            display: { xs: 'block', sm: 'none' },
                          }}
                        >
                          Arabic
                        </Typography>
                        <TextField
                          size='small'
                          fullWidth
                          placeholder='Arabic'
                          value={row.ar}
                          dir='rtl'
                          onChange={(e) =>
                            handleRowChange(index, 'ar', e.target.value)
                          }
                        />
                      </Box>

                      <IconButton
                        aria-label='Remove word'
                        onClick={() => handleRemoveRow(index)}
                        sx={{
                          width: 40,
                          height: 40,
                          alignSelf: { xs: 'flex-end', sm: 'center' },
                        }}
                      >
                        <CloseIcon fontSize='small' />
                      </IconButton>
                    </Stack>
                  ))}
                </Stack>

                <Button
                  fullWidth
                  variant='text'
                  startIcon={<AddIcon />}
                  onClick={handleAddRow}
                  sx={{
                    mt: 1.5,
                    py: 1.25,
                    borderStyle: 'dashed',
                    borderWidth: 1,
                    borderColor: 'divider',
                    color: 'text.secondary',
                    justifyContent: 'center',
                    '&:hover': {
                      borderColor: 'text.disabled',
                      bgcolor: 'action.hover',
                    },
                  }}
                >
                  Add word
                </Button>
              </Box>

              <Stack direction='row' spacing={2} justifyContent='flex-end'>
                <Button
                  variant='outlined'
                  disabled={!isDirty || isSaving}
                  onClick={handleCancel}
                >
                  Cancel
                </Button>
                <LoadingButton
                  variant='contained'
                  loading={isSaving}
                  disabled={!isDirty}
                  onClick={handleSave}
                >
                  Save
                </LoadingButton>
              </Stack>
            </Stack>
          )}
        </Card>
      </Container>
    </Page>
  );
};

export default DynamicWordPage;
